import { Component, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PlatformUsersService } from '../../../api/api-services/platform-users.service';
import { AccountDeleteService } from '../../../api/api-services/account-delete.service';
import { PlatformUserActivationLinkService } from '../../../api/api-services/platform-user-activation-link.service';
import { IPlatformUserAccountVm } from '../../../api/platform-user-account-vm.model';
import { SortOrder } from '../../../api/sort-order.model';
import { UsersDetailsComponent } from './users-details/users-details.component';
import { UsersAddSubscriptionsComponent } from './users-add-subscriptions/users-add-subscriptions.component';
import { confirmAndResendActivationLink } from './activation-link';

@Component({
  selector: 'app-platform-users',
  templateUrl: './platform-users.component.html',
  styleUrl: './platform-users.component.scss'
})
export class PlatformUsersComponent implements OnDestroy {
  private readonly platformUsersService = inject(PlatformUsersService);
  private readonly accountDeleteService = inject(AccountDeleteService);
  private readonly activationLinkService = inject(PlatformUserActivationLinkService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly dialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly searchChanges = new Subject<string>();
  private readonly subscriptions = new Subscription();
  private request?: Subscription;
  private dialog?: DynamicDialogRef;
  // Account to open once the list is loaded, from a link in problem reports (?search=<e-mail>&user=<guid>).
  private pendingDetailsUserGuid: string | null = null;

  users: IPlatformUserAccountVm[] = [];
  search = '';
  loading = false;
  first = 0;
  rows = 50;
  totalRecords = 0;
  /** Guids of the accounts whose activation link is being sent. */
  readonly activationLinkPending = new Set<string>();

  readonly columns = [
    { field: 'guid', header: 'ID', columnClass: 'users-id-cell', valueFunction: (user: IPlatformUserAccountVm) => user.guid.slice(-6),
      actions: [{ icoSuffix: 'pi pi-copy', tooltip: 'Kopiuj ID', buttonClass: 'transparent-button appear',
        callback: (user: IPlatformUserAccountVm) => navigator.clipboard.writeText(user.guid) }] },
    { field: 'email', header: 'Adres email', customTemplate: 'userEmail' },
    { field: 'companyName', header: 'Firma' },
    { field: 'displayName', header: 'Nazwa użytkownika (Imię i nazwisko)' },
    { field: 'oskName', header: 'OSK', customTemplate: 'userOsk' },
    { field: 'companyRole', header: 'Rola' },
    { field: 'created', header: 'Data utworzenia', dateField: true,
      valueFunction: (user: IPlatformUserAccountVm) => this.validDate(user.created) },
    { field: 'lastLoginDate', header: 'Ostatnie logowanie', dateField: true,
      valueFunction: (user: IPlatformUserAccountVm) => this.validDate(user.lastLoginDate) },
    { field: 'loginCount', header: 'Logowania' },
    { field: 'subscriptionPlan', header: 'Pakiet' },
    { field: 'subscriptionPlanActiveTo', header: 'Pakiet aktywny do', dateField: true },
    // A template rather than table actions: the activation link is offered only to accounts that are not active yet.
    { field: 'actions', header: 'Akcje', columnClass: 'white-space-nowrap', customTemplate: 'userActions' }
  ];

  constructor() {
    const queryParams = this.activatedRoute.snapshot.queryParamMap;
    this.search = queryParams.get('search') ?? '';
    this.pendingDetailsUserGuid = queryParams.get('user');
    this.subscriptions.add(this.searchChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => this.resetAndLoad()));
  }

  onSearch(value: string): void {
    this.search = value;
    this.searchChanges.next(value);
  }

  resetAndLoad(): void {
    this.first = 0;
    this.load({ page: 1, pageSize: this.rows });
  }

  load(event?: { page: number; pageSize: number }): void {
    const page = event?.page ?? Math.floor(this.first / this.rows) + 1;
    this.rows = event?.pageSize ?? this.rows;
    this.first = (page - 1) * this.rows;
    this.request?.unsubscribe();
    this.loading = true;
    this.request = this.platformUsersService.get(this.search, 'created', page, this.rows,
      SortOrder.Descending).subscribe({
      next: result => {
        this.users = result.data ?? [];
        this.totalRecords = result.allElementsCount;
        this.loading = false;
        this.openPendingDetails();
      },
      error: () => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Błąd', detail: 'Nie udało się pobrać użytkowników.' });
      }
    });
  }

  showDetails(user: IPlatformUserAccountVm, initialTab?: 'devices'): void {
    this.dialog = this.dialogService.open(UsersDetailsComponent, {
      showHeader: false, closable: false, closeOnEscape: true, position: 'right',
      width: '968px', height: '100vh', breakpoints: { '968px': '100vw' },
      data: { currentUserIndex: this.users.indexOf(user), usersListPerPage: this.users, editMode: false, initialTab }
    });
  }

  private openPendingDetails(): void {
    const userGuid = this.pendingDetailsUserGuid;
    if (!userGuid) {
      return;
    }
    this.pendingDetailsUserGuid = null;
    const user = this.users.find(item => item.guid === userGuid);
    if (user) {
      this.showDetails(user, 'devices');
    } else {
      this.messageService.add({ severity: 'warn', summary: 'Nie znaleziono konta',
        detail: 'Konto ze zgłoszenia nie pasuje do wyszukiwania — mogło zostać usunięte lub zmienić adres e-mail.' });
    }
  }

  assignSubscription(user: IPlatformUserAccountVm): void {
    this.dialog = this.dialogService.open(UsersAddSubscriptionsComponent, {
      showHeader: false, closable: false, closeOnEscape: true, width: '480px',
      breakpoints: { '480px': '100vw' }, data: { user }
    });
    this.subscriptions.add(this.dialog.onClose.subscribe(result => { if (result) this.load(); }));
  }

  deleteUser(user: IPlatformUserAccountVm): void {
    this.confirmationService.confirm({
      key: 'platformUserDelete', header: 'Usuwanie użytkownika',
      message: `Czy na pewno chcesz usunąć konto ${user.email}? Konto bez danych zostanie usunięte trwale. W przeciwnym razie zostanie zablokowane, a adres e-mail zwolniony.`,
      acceptLabel: 'Usuń', rejectLabel: 'Anuluj', defaultFocus: 'reject',
      accept: () => this.subscriptions.add(this.accountDeleteService.delete(undefined, undefined, user.guid).subscribe({
        next: () => { this.messageService.add({ severity: 'success', summary: 'Konto usunięte' }); this.resetAndLoad(); },
        error: () => this.messageService.add({ severity: 'error', summary: 'Nie udało się usunąć konta' })
      }))
    });
  }

  resendActivationLink(user: IPlatformUserAccountVm): void {
    confirmAndResendActivationLink(user, { confirmationKey: 'platformUserActivationLink', pending: this.activationLinkPending,
      subscriptions: this.subscriptions, confirmationService: this.confirmationService,
      messageService: this.messageService, activationLinkService: this.activationLinkService });
  }

  private validDate(value?: Date): Date | null {
    return value && new Date(value).getFullYear() > 1 ? value : null;
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
    this.subscriptions.unsubscribe();
    this.dialog?.close();
  }
}
